import { useParams } from "react-router-dom";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { ArrowForwardIos } from "@mui/icons-material";

import { OrdersStorage } from "./OnpLocalStorage";
import { OnpContext } from "./OnpLayout";
import OrderDetailsModal from "./OrderDetailsModal";
import { translate } from "../../i18n/customI18nProvider";

let ordersStorageInstance = null;

export default function OnpOrders() {
    const { barId } = useParams();
    const { barInfo } = useContext(OnpContext) || { barInfo: {} };

    const [orders, setOrders] = useState([]);

    const [lastSeen, setLastSeen] = useState(null);

    const [selectedOrderId, setSelectedOrderId] = useState(null);

    const [animatedIds, setAnimatedIds] = useState(new Set());

    useEffect(() => {
        if(!barInfo.eventId) return;

        ordersStorageInstance = new OrdersStorage(barInfo.eventId);
        setOrders(ordersStorageInstance.getOrders() || []);
        setLastSeen(ordersStorageInstance.getLastSeenOrder());
        let active = true;

        const poll = async () => {
            if (!active) return;

            await ordersStorageInstance.checkPendingOrders();
            const fresh = ordersStorageInstance.getOrders() || [];
            setOrders(fresh);

            setTimeout(poll, 15000);
        };

        poll();

        return () => {
            active = false;
        };
    }, [barInfo])

    const newOrderIds = useMemo(() => {
        if (!orders.length || !lastSeen) return new Set();

        const ids = new Set();

        for (let i = orders.length - 1; i >= 0; i--) {
            if (orders[i].uid === lastSeen) break;
            ids.add(orders[i].uid);
        }

        return ids;
    }, [orders, lastSeen]);

    useEffect(() => {
        if (newOrderIds.size === 0) return;

        const ids = Array.from(newOrderIds);

        requestAnimationFrame(() => {
            setAnimatedIds(prev => new Set([...prev, ...ids]));
        });
    }, [newOrderIds]);

    useEffect(() => {
        if (!orders.length) return;

        const newestUid = orders[orders.length - 1].uid;

        if (newestUid !== lastSeen) {
            ordersStorageInstance.setLastSeenOrder(newestUid);
            setLastSeen(newestUid);
        }
    }, [orders]);

    return (
        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: "300px", scrollbarWidth: 'none', maxHeight: 'calc(100vh - 500px)' }}>
            <Grid container spacing={2}>
                {orders.length === 0 && (
                    <Box textAlign="center" py={6} width="100%">
                        <Typography variant="h6" color="text.secondary">
                            {translate("custom.no_orders_yet")}
                        </Typography>
                    </Box>
                )}

                {orders.slice().reverse().map((order) => (
                    <Grid item xs={12} key={order.uid}>
                        <div
                            className={`order-card ${
                                animatedIds.has(order.uid) ? "highlight" : ""
                            }`}
                        >
                            <Card
                                sx={{ cursor: "pointer" }}
                                onClick={() => setSelectedOrderId(order.uid)}
                            >
                                <CardContent>
                                    <Grid
                                        container
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Grid item>
                                            <Typography variant="h6">
                                                #
                                                {order.number ??
                                                    order.uid?.slice(0, 8) ??
                                                    "-"}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {order.createdAt ?? "-"}
                                            </Typography>
                                        </Grid>

                                        <Grid item>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={2}
                                            >
                                                <Typography variant="h6">
                                                    CHF{" "}
                                                    {order.total?.toFixed(2) ??
                                                        "0.00"}
                                                </Typography>
                                                <ArrowForwardIos
                                                    fontSize="small"
                                                    color="action"
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </div>
                    </Grid>
                ))}
            </Grid>

            <OrderDetailsModal
                orderId={selectedOrderId}
                userId={barId}
                onClose={() => setSelectedOrderId(null)}
            />
        </div>
    );
}